/**
 * Device Filters Component
 * Advanced filtering and search for devices
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
  styled,
  SelectChangeEvent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { colors } from '../../theme/colors';

interface DeviceFiltersProps {
  devices: any[];
  onFilterChange: (filtered: any[]) => void;
  deviceFamilies?: string[];
}

const FiltersContainer = styled(Box)({
  display: 'flex',
  gap: 16,
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: '20px 0',
  marginBottom: 32,
});

const SearchField = styled(TextField)({
  flex: '1 1 400px',
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: colors.background.paper,
    transition: 'all 0.2s ease-in-out',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.neutral[200],
    },
    '&:hover': {
      backgroundColor: colors.neutral[50],
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.neutral[300],
      },
    },
    '&.Mui-focused': {
      backgroundColor: colors.background.paper,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.primary.main,
        borderWidth: 2,
      },
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '0.9375rem',
  },
});

export const DeviceFilters: React.FC<DeviceFiltersProps> = ({ devices, onFilterChange, deviceFamilies = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [familyFilter, setFamilyFilter] = useState<string>('all');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Apply filters whenever filter values or devices change
  useEffect(() => {
    const filtered = devices.filter((device) => {
      // Search filter (searches across multiple fields)
      const searchLower = debouncedSearchQuery.toLowerCase();
      const matchesSearch =
        !debouncedSearchQuery ||
        device.id?.toLowerCase().includes(searchLower) ||
        device.device_family_name?.toLowerCase().includes(searchLower) ||
        device.description?.toLowerCase().includes(searchLower) ||
        device.device_owner_id?.toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && device.is_active) ||
        (statusFilter === 'inactive' && !device.is_active);

      // Family filter
      const matchesFamily = familyFilter === 'all' || device.device_family_name === familyFilter;

      return matchesSearch && matchesStatus && matchesFamily;
    });

    onFilterChange(filtered);
  }, [debouncedSearchQuery, statusFilter, familyFilter, devices, onFilterChange]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setFamilyFilter('all');
  };

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all' || familyFilter !== 'all';

  // Get unique device families from devices
  const uniqueFamilies = useMemo(() => {
    if (deviceFamilies.length > 0) return deviceFamilies;
    return Array.from(new Set(devices.map((d) => d.device_family_name).filter(Boolean)));
  }, [devices, deviceFamilies]);

  return (
    <FiltersContainer>
      {/* Search Field */}
      <SearchField
        placeholder="Поиск по ID, семейству, описанию..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: colors.text.secondary }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Status Filter */}
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Статус</InputLabel>
        <Select
          value={statusFilter}
          label="Статус"
          onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
        >
          <MenuItem value="all">Все</MenuItem>
          <MenuItem value="active">Активные</MenuItem>
          <MenuItem value="inactive">Неактивные</MenuItem>
        </Select>
      </FormControl>

      {/* Family Filter */}
      {uniqueFamilies.length > 0 && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Семейство</InputLabel>
          <Select
            value={familyFilter}
            label="Семейство"
            onChange={(e: SelectChangeEvent) => setFamilyFilter(e.target.value)}
          >
            <MenuItem value="all">Все семейства</MenuItem>
            {uniqueFamilies.map((family) => (
              <MenuItem key={family} value={family}>
                {family}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Chip
          label="Очистить фильтры"
          onDelete={handleClearFilters}
          deleteIcon={<ClearIcon />}
          sx={{
            backgroundColor: colors.neutral[100],
            '&:hover': {
              backgroundColor: colors.neutral[200],
            },
          }}
        />
      )}
    </FiltersContainer>
  );
};
